import { CheckmarkCircle01Icon, CircleIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
/* eslint-disable react-hooks/exhaustive-deps */
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { createContext, ReactNode, useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import useSnackbar from "@/hooks/useSnackbar";
import { useLocation } from "react-router-dom";
import useRoutes from "@/hooks/useRoutes";
import { useCreateProfileMutation, useLazyFetchProfileQuery } from "@/api/usersApi";
import { useCreateAccessMutation } from "@/api/accessApi";
import { useCreateOrganizationMutation, useDeleteOrganizationMutation, useUpdateOrganizationMutation } from "@/features/organizations/api/organizationsApi";
import { useAddMenuMutation, useLazyFetchMenusQuery } from "@/features/organizations/api/menusApi";
import { DEFAULT_MENU_TEMPLATE, DEFAULT_WEBSITE_TEMPLATE } from "@/constants/configs";
import {
  useCreateWebsiteMutation,
  useCreateOrganizationAliasMutation,
  useLazyFetchOrganizationWebsitesQuery,
  useLazyFetchOrganizationAliasQuery,
} from "@/features/organizations/api/websiteApi";
import { Organization, PlanType } from "@/types";
import { useAppDispatch } from "@/hooks";
import { uploadOrganizationImage } from "@/features/organizations/slice";
import ReactPixel from "react-facebook-pixel";
import { Legal } from "@/types/legal";
export interface OnboardingFormValues {
  industry: string;
  alias: string;
  title: string;
  primaryColor: string;
  secondaryColor: string;
  facebook?: string;
  instagram?: string;
  x?: string;
  tiktok?: string;
  whatsapp: string;
  plan: string;
  legal?: Legal;
}
export interface Step {
  title?: string;
  label: string;
  component: ReactNode;
  hideContinueButton?: boolean;
  schema?: Yup.ObjectSchema<any>;
}

interface OnboardingContext {
  steps: Step[];
  validateStep: (stepId: string) => void;
}

export const OnboardingContext = createContext<OnboardingContext>({
  steps: [],
  validateStep: () => undefined,
});

interface Task {
  id: string;
  label: string;
  isCompleted: boolean;
}

const SetupStep = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [openSnackbar] = useSnackbar();
  const [navigate] = useRoutes();
  const [activeTask, setActiveTask] = useState(0);
  const [tasks, setTasks] = useState<Task[]>(() => {
    const industry = location.state?.industry;
    const isOther = industry === "other";
    return [
      {
        id: "user",
        label: "Creando usuario",
        isCompleted: false,
      },
      {
        id: "organization",
        label: isOther ? "Construyendo tu comercio" : "Construyendo tu restaurante",
        isCompleted: false,
      },
      {
        id: "menu",
        label: isOther ? "Diseñando el catálogo" : "Diseñando el menú",
        isCompleted: false,
      },
      {
        id: "website",
        label: "Activando tu sitio web",
        isCompleted: false,
      },
      {
        id: "profile",
        label: "Publicando el perfil",
        isCompleted: false,
      },
    ];
  });
  const task = tasks[activeTask];
  // Reliable ref to hold the created organizationId across render cycles
  const createdOrgIdRef = useRef<string | null>(null);

  // profiles
  const [fetchProfile] = useLazyFetchProfileQuery();
  const [addProfile] = useCreateProfileMutation();
  // access
  const [addAccess] = useCreateAccessMutation();
  // organizations
  const [addOrganization, organization] = useCreateOrganizationMutation();
  const [deleteOrganization] = useDeleteOrganizationMutation();
  const [updateOrganization] = useUpdateOrganizationMutation();
  // menus
  const [fetchMenus] = useLazyFetchMenusQuery();
  const [addMenu] = useAddMenuMutation();
  // websites
  const [fetchWebsites] = useLazyFetchOrganizationWebsitesQuery();
  const [addWebsite] = useCreateWebsiteMutation();
  // profile
  const [fetchAlias] = useLazyFetchOrganizationAliasQuery();
  const [addAlias, alias] = useCreateOrganizationAliasMutation();

  const completeTask = (taskIndex?: number) => {
    const draft = [...tasks];

    draft.splice(taskIndex ?? activeTask, 1, {
      ...task,
      isCompleted: true,
    });

    setTasks(draft);
    setActiveTask((prev) => prev + 1);
  };

  const createUserProfile = async () => {
    const userId = location.state?.user?.uid ?? null;

    if (!userId) return;

    const profile = await fetchProfile(userId);

    if (profile?.error?.status === 404) {
      const profile = await addProfile(location.state.user);
      if (!profile.error) completeTask();
    } else if (profile?.data) {
      completeTask();
    }
  };

  const createOrganization = async () => {
    const userId = location.state?.user?.uid ?? null;

    if (!userId) return;
    const legalState = location.state?.legal;

    const payload: Partial<Organization> = {
      title: location.state?.title,
      primaryColor: location.state?.primaryColor,
      secondaryColor: location.state?.secondaryColor,
      industry: location.state?.industry === "other" ? "other" : "food-and-beverages",

      legal: legalState ? {
        terms: {
          accepted: true,
          version: legalState.terms?.version || "1.0.0",
          acceptedAt: legalState.terms?.acceptedAt || Date.now(),
          acceptedBy: legalState.terms?.acceptedBy || userId,
        },
        privacy: {
          accepted: true,
          version: legalState.privacy?.version || "1.0.0",
          acceptedAt: legalState.privacy?.acceptedAt || Date.now(),
          acceptedBy: legalState.privacy?.acceptedBy || userId,
        },
        marketing: {
          accepted: !!legalState.marketing?.accepted,
          ...(legalState.marketing?.accepted ? {
            acceptedAt: legalState.marketing.acceptedAt || Date.now(),
            acceptedBy: legalState.marketing.acceptedBy || userId,
          } : {}),
        },
      } : undefined,
    };

    if (location.state?.facebook) payload.facebook = location.state?.facebook;
    if (location.state?.instagram) payload.instagram = location.state?.instagram;
    if (location.state?.x) payload.x = location.state?.x;
    if (location.state?.tiktok) payload.tiktok = location.state?.tiktok;

    const organization = await addOrganization({
      ...payload,
      plan: PlanType.FREE,
    });

    if (organization?.data?.id) {
      createdOrgIdRef.current = organization.data.id;
    }

    if (organization.error) {
      openSnackbar("Error creando comercio", "error");
    } else {
      const access = await addAccess({
        organizationId: organization.data?.id,
        organizationTitle: organization.data?.title,
      });

      if (access.error) {
        openSnackbar("Error creando comercio", "error");
        await deleteOrganization(organization.data?.id);
      } else if (organization?.data?.id) {
        // Upload logos
        const logosPayload = {
          logo: null,
          isotype: null,
        }

        if (location.state?.logo) {
          const logo = await dispatch(uploadOrganizationImage({
            organizationId: organization.data.id,
            file: location.state?.logo
          }));

          if (logo?.payload?.image) {
            logosPayload.logo = logo?.payload?.image;
          }
        }

        if (location.state?.isotype) {
          const isotype = await dispatch(uploadOrganizationImage({
            organizationId: organization.data.id,
            file: location.state?.isotype
          }));

          if (isotype?.payload?.image) {
            logosPayload.isotype = isotype?.payload?.image;
          }
        }

        await updateOrganization({
          organizationId: organization.data.id,
          payload: logosPayload,
        });
      }
    }

    completeTask();
  };

  const createMenu = async () => {
    const orgId = createdOrgIdRef.current ?? organization?.data?.id;
    if (!orgId) return;

    const results = await fetchMenus(orgId);
    const isOther = location.state?.industry === "other";

    if (results?.error?.status === 404 || (results?.data && (results.data as any).length === 0)) {
      const menu = await addMenu({
        draft: {
          title: isOther ? "Catálogo Principal" : "Menú Principal",
          description: isOther
            ? "Catálogo de ejemplo, edítalo o elimínalo lo antes posible"
            : "Menú de ejemplo, edítalo o elimínalo lo antes posible",
          isPublished: true,
          template: DEFAULT_MENU_TEMPLATE,
        },
        organizationId: orgId,
      });

      if (menu.error) {
        openSnackbar(isOther ? "Error creando catálogo" : "Error creando menú", "error");
      } else {
        completeTask();
      }
    } else {
      completeTask();
    }
  };
  const createWebsite = async () => {
    const orgId = createdOrgIdRef.current ?? organization?.data?.id;
    if (!orgId) return;

    const results = await fetchWebsites(orgId);

    if (results?.error?.status === 404 || (results?.data && (results.data as any).length === 0)) {
      const website = await addWebsite({
        organizationId: orgId,
        website: {
          title: "Sitio principal",
          template: DEFAULT_WEBSITE_TEMPLATE,
          isPublished: true,
        },
      });

      if (website.error) {
        openSnackbar("Error creando sitio web", "error");
      } else {
        await updateOrganization({
          organizationId: organization.data.id,
          payload: {
            website: website.data, // website id
          },
        });

        completeTask();
      }
    } else {
      completeTask();
    }
  };

  const createProfile = async () => {
    const alias = location.state?.alias ?? null;
    const orgId = createdOrgIdRef.current ?? organization?.data?.id;

    if (!orgId || !alias) return;

    const results = await fetchAlias(alias);

    if (results?.error?.status === 404) {
      const result = await addAlias({
        organizationId: orgId,
        alias,
      });

      if (result.error) {
        openSnackbar("Error creando el perfil", "error");
      } else {
        await updateOrganization({
          organizationId: orgId,
          payload: {
            alias,
          },
        });

        completeTask();
      }
    } else {
      completeTask();
    }
  };

  useEffect(() => {
    switch (task?.id) {
      case "user":
        createUserProfile();
        break;
      case "organization":
        createOrganization();
        break;
      case "menu":
        createMenu();
        break;
      case "website":
        createWebsite();
        break;
      case "profile":
        createProfile();
        break;
      default:
        return;
    }
  }, [task?.id]);

  useEffect(() => {
    if (!location.state) {
      navigate("onboarding.main");
    }
  }, [location.state]);

  useEffect(() => {
    if (tasks.every((task) => task.isCompleted)) {
      ReactPixel.track('CompleteRegistration');

      navigate("onboarding.finished", {}, {
        state: {
          ...alias?.data,
          industry: location.state?.industry,
        }
      });
    }
  }, [tasks, organization]);

  return (
    <Stack spacing={1}>
      {tasks.map((task, taskIndex) => {
        const isActivestep = activeTask === taskIndex;

        return (
          <Box key={task.id} sx={{ paddingX: 3, paddingY: 2.5, borderRadius: 3, backgroundColor: isActivestep ? "rgba(255,255,255,0.1)" : "transparent" }}>
            <Stack spacing={2} direction="row" justifyContent="space-between">
              <Typography variant="h4" color="white">
                {task.label}
              </Typography>
              {isActivestep ? (
                <CircularProgress color="secondary" size={24} />
              ) : task.isCompleted ? (
                <HugeiconsIcon icon={CheckmarkCircle01Icon} color="#D0F857" />
              ) : (
                <HugeiconsIcon icon={CircleIcon} color="rgba(255,255,255,0.25)" />
              )}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

export default SetupStep;
